import { useCallback, useEffect, useState } from 'react';
import { Badge, EmptyState, Icon, useLatest } from '@orgazone/ui-web';
import { api, type FiscalHealthView, type MissingClosure, type RegisterChainHealth, type RegisterLiveness, type SignatureHealthView } from '../api';
import { useStoreScope } from '../store-context';
import { codeCaisse } from '../caisse';
import { currentLang } from '../lang';
import { t } from '../i18n';

/** Fond, bordure et rayon communs aux sections — le dessin de carte du back-office. */
const CARTE: React.CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
  padding: 16, marginBottom: 16,
};

const TH: React.CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontSize: '11.5px', fontWeight: 700,
  letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--ink-500)',
  borderBottom: '1px solid var(--ink-200)', whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '9px 10px', fontSize: 'var(--t-body)', color: 'var(--ink-800)',
  borderBottom: '1px solid var(--ink-100)', verticalAlign: 'middle',
};

/**
 * SANTÉ NF525 — le poste de conformité : ce que le siège CONSTATE sur la chaîne fiscale du magasin.
 *
 * UTILITÉ : le §O.7 fait du back-office un acteur du périmètre certifié — il doit **prouver qu'il
 * surveille** l'intégrité de ce que les caisses remontent. Cet écran est cette preuve.
 *
 * ⚠️ 🔴 IL AFFICHE CE QU'IL NE PROUVE PAS, EN HAUT ET SANS DÉTOUR. Le constat couvre la continuité de
 * séquence et de chaînage, et la signature de chaque pièce dont la caisse a remonté sa clé ; une pièce sans
 * cette clé n'est ni prouvée ni accusée, et une signature valide ne dit pas ce qui s'est passé au comptoir.
 * Un « chaîne saine » sans ces réserves se lirait comme une preuve que nous ne pouvons pas donner (§23.0).
 * Les phrases viennent du SERVEUR (`limits`) : rédigées ici, elles seraient une seconde liste, et celle
 * qui oublierait une ligne serait la plus rassurante.
 *
 * ⚠️ 🔴 TROIS ÉTATS DE BATTEMENT, JAMAIS DEUX : en ligne · hors ligne · **jamais vue**. « Hors ligne »
 * se dit d'une caisse qui a travaillé puis s'est tue ; une caisse déclarée mais jamais vue est le cas
 * qu'on cherche le jour de sa mise en service. Les confondre ferait passer une mise en service ratée
 * pour une panne de réseau.
 *
 * ⚠️ LES DEUX TABLEAUX NE SE FONDENT PAS. Le battement vient du registre des APPAREILS, le constat de
 * chaîne des PIÈCES : une caisse peut être dans l'un sans être dans l'autre (une caisse neuve déclarée
 * qui n'a rien vendu · une caisse qui a vendu sans être déclarée). Les joindre en un seul tableau
 * obligerait à inventer une ligne pour l'absente.
 *
 * ⚠️ 🔴 LES CLÔTURES MANQUANTES ONT DEUX ÉTATS, ET C'EST TOUT LEUR INTÉRÊT. Un mois non clos dont la
 * caisse n'a plus rien scellé ATTEND simplement sa prochaine activité — c'est le cas du magasin
 * saisonnier, et il n'y a rien à réparer. Un mois non clos alors que la caisse a continué de sceller est
 * une anomalie. N'afficher qu'« en retard » ferait clignoter l'écran le 1er de chaque mois, et un
 * tableau de bord qui crie pour du normal s'apprend à être ignoré.
 *
 * ⚠️ 🔴 LES HORLOGES SE CONSTATENT PAR LES DEUX TEMPS QUE LA CAISSE ÉCRIT ELLE-MÊME, et l'un des deux
 * contrôles peut ne PAS AVOIR EU LIEU : l'écart exige le fuseau du magasin. La section le dit alors
 * explicitement — « pas mesuré » n'est pas « zéro anomalie », et un vert sur un contrôle qui ne s'est pas
 * fait est la façon la plus discrète de mentir.
 *
 * ⚠️ CE QUI N'EST PAS ICI, ET POURQUOI. Les **alertes** (RAP-5.5) : il n'existe aucun mécanisme de
 * notification dans le back-office. Ce qui n'est pas branché s'enlève, il ne se simule pas.
 */
export function SanteNf525() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [vue, setVue] = useState<FiscalHealthView | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT L'ÉCRAN : la santé de l'ancien magasin, revenue après celle du nouveau,
  //    s'afficherait sous son bandeau — un vert qui ne le décrit pas.
  const latest = useLatest();
  const charger = useCallback(() => {
    if (!gln) return;
    const isLatest = latest();
    setChargement(true);
    setErreur(null);
    api.fiscalHealth()
      .then((v) => { if (!isLatest()) return; setVue(v); setChargement(false); })
      // ⚠️ LE MESSAGE RÉEL, et l'état de succès NON appliqué : une santé qu'on n'a pas pu lire ne doit
      //    jamais ressembler à une santé bonne — c'est le seul écran dont c'est tout le métier.
      .catch((e: unknown) => {
        if (!isLatest()) return;
        setVue(null);
        setErreur(e instanceof Error ? e.message : String(e));
        setChargement(false);
      });
  }, [gln, latest]);

  useEffect(charger, [charger]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            {t('Santé NF525')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            {t('Ce que le siège constate sur la chaîne fiscale de ce magasin — et ce qu’il ne peut pas constater.')}
          </p>
        </div>
      </div>

      {erreur ? (
        <div role="alert" style={{ ...CARTE, display: 'flex', gap: 10, alignItems: 'flex-start', borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}>
          <Icon name="alert" size={17} stroke={2.1} />
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>{erreur}</span>
        </div>
      ) : null}

      {chargement ? (
        <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture de la chaîne…')}</p>
      ) : !vue ? null : (
        <>
          <Limites limits={vue.limits} />
          <VerdictChaine chain={vue.chain} />
          <Signatures signatures={vue.signatures} onVerified={charger} />
          <Clotures closures={vue.closures} />
          <Horloges clock={vue.clock} />
          <Battement registers={vue.registers} seuil={vue.onlineAfterMinutes} />
          <ChaineParCaisse registers={vue.chain.registers} />
          <Archives archives={vue.archives} />
        </>
      )}
    </>
  );
}

/**
 * CE QUE CE CONSTAT NE PROUVE PAS — en tête, pas en note de bas de page.
 *
 * ⚠️ 🔴 EN HAUT ET AVANT LE VERDICT, délibérément : lues après un bandeau vert, ces phrases ne changent
 * plus l'idée qu'on s'est faite. C'est le seul endroit de l'application où une réserve doit précéder la
 * bonne nouvelle.
 */
function Limites({ limits }: { limits: string[] }) {
  if (limits.length === 0) return null;
  return (
    <section style={{ ...CARTE, borderColor: 'var(--amber-fg)', background: 'var(--amber-bg)' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="info" size={17} stroke={2.1} />
        {t('Ce que ce constat ne prouve pas')}
      </h2>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {limits.map((l) => (
          <li key={l} style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.55 }}>{l}</li>
        ))}
      </ul>
    </section>
  );
}

/**
 * LE VERDICT DU MAGASIN — et ce qui est compté À PART.
 *
 * ⚠️ LA REPRISE ET L'ORIGINE INCONNUE SONT DEUX LIGNES DISTINCTES. La reprise est NORMALE (sa chaîne
 * n'est pas la nôtre) ; une origine inconnue ne l'est pas — c'est une pièce dont personne ne sait qui
 * l'a signée, et la fondre dans la reprise la ferait disparaître.
 */
function VerdictChaine({ chain }: { chain: FiscalHealthView['chain'] }) {
  const aJuger = chain.registers.length;
  const ko = !chain.ok;
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Verdict de la chaîne')}
      </h2>
      <div style={{
        display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 12px', borderRadius: 'var(--r-sm)',
        background: ko ? 'var(--danger-bg)' : aJuger === 0 ? 'var(--ink-100)' : 'var(--teal-100)',
        color: ko ? 'var(--ink-900)' : aJuger === 0 ? 'var(--ink-700)' : 'var(--teal-800)',
      }}>
        <Icon name={ko ? 'alert' : aJuger === 0 ? 'info' : 'check'} size={16} stroke={2.2} />
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>
          {/* ⚠️ « AUCUNE CHAÎNE À VÉRIFIER » N'EST PAS « TOUT VA BIEN » : un magasin dont aucune caisse
              n'a encore scellé n'a rien de sain ni de fautif — il n'a rien. Afficher un vert
              affirmerait un contrôle qui n'a pas eu lieu. */}
          {aJuger === 0
            ? t('Aucune chaîne de cette caisse à vérifier — aucune pièce scellée par nos caisses sur ce magasin.')
            : ko
              ? t('{n} caisse présente un défaut de chaîne.', { n: chain.registers.filter((r) => !r.ok).length })
              : t('{n} caisse vérifiée : séquence et chaînage continus.', { n: aJuger })}
        </span>
      </div>

      {(chain.legacyPieces > 0 || chain.unknownAlgoPieces > 0) ? (
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 12 }}>
          {chain.legacyPieces > 0 ? (
            <Badge tone="neutral">
              {t('{n} pièce reprise, non jugée', { n: chain.legacyPieces })}
            </Badge>
          ) : null}
          {/* ⚠️ Une origine INCONNUE n'est pas de la reprise : c'est une pièce dont personne ne sait qui
              l'a signée. Ton d'alerte, et jamais fondue dans la ligne précédente. */}
          {chain.unknownAlgoPieces > 0 ? (
            <Badge tone="warn" dot>
              {t('{n} pièce d’origine inconnue', { n: chain.unknownAlgoPieces })}
            </Badge>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * LES CLÔTURES MENSUELLES QUI MANQUENT AU SIÈGE.
 *
 * ⚠️ 🔴 DEUX ÉTATS, ET C'EST TOUT L'INTÉRÊT DE LA SECTION. « En attente » = la caisse n'a plus rien
 * scellé depuis la fin du mois, donc la clôture « par l'usage » n'a pas eu sa chance : c'est le magasin
 * saisonnier, et il n'y a rien à réparer. « En retard » = la caisse a continué de sceller APRÈS la fin
 * du mois et la clôture manque quand même. Tout afficher en rouge ferait clignoter l'écran le 1er de
 * chaque mois, et un tableau de bord qui crie pour du normal s'apprend à être ignoré (§23.0).
 *
 * ⚠️ AUCUN BOUTON ICI, ET C'EST DÉLIBÉRÉ : le siège ne peut pas clôturer à la place d'une caisse — elle
 * seule détient la clé de sa chaîne (§9.4.A). Un bouton « clôturer » serait un bouton qui fait semblant.
 *
 * ⚠️ L'ORDRE RESTE CHRONOLOGIQUE, du plus ancien au plus récent, même si le plus grave est ailleurs :
 * c'est le mois le plus ancien qui borne ce qu'on peut archiver et exporter.
 */
function Clotures({ closures }: { closures: FiscalHealthView['closures'] }) {
  const { enRetard, enAttente } = bilanClotures(closures.missing);
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Clôtures mensuelles')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {t('La caisse produit sa clôture mensuelle à sa prochaine activité. Le siège ne clôture rien : il constate ce qui manque.')}
      </p>

      {/* ⚠️ LE REPLI DE FUSEAU SE DIT : c'est lui qui décide quel mois est encore OUVERT, donc ce qui
          est réclamé. Un magasin hors métropole verrait sinon un constat calculé dans le fuseau d'un
          autre pays sans que rien ne le signale. */}
      {!closures.timezoneSet ? (
        <p style={{ margin: '0 0 12px', fontSize: '12.5px', color: 'var(--ink-700)', display: 'flex', gap: 7, alignItems: 'flex-start' }}>
          <Icon name="clock" size={15} stroke={2.1} />
          <span>
            {t('Ce magasin n’a pas de fuseau horaire réglé : {v} est appliqué pour savoir quel mois est encore ouvert. Posez-le dans Réglages → Calendrier des envois.', { v: closures.timezone })}
          </span>
        </p>
      ) : null}

      {closures.missing.length === 0 ? (
        <EmptyState
          icon="calendar"
          title={t('Aucune clôture mensuelle ne manque')}
          note={t('Tous les mois révolus travaillés par les caisses de ce magasin ont leur clôture au siège.')}
        />
      ) : (
        <>
          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 12px', borderRadius: 'var(--r-sm)',
            marginBottom: 12,
            background: enRetard > 0 ? 'var(--danger-bg)' : 'var(--amber-bg)',
            color: 'var(--ink-900)',
          }}>
            <Icon name={enRetard > 0 ? 'alert' : 'info'} size={16} stroke={2.2} />
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>
              {enRetard > 0
                ? t('{n} mois est resté sans clôture alors que la caisse a continué de sceller.', { n: enRetard })
                : t('{n} mois attend la prochaine activité de sa caisse : rien n’est cassé, mais la période n’est ni archivable ni exportable.', { n: enAttente })}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>{t('Mois')}</th>
                  <th style={TH}>{t('Caisse')}</th>
                  <th style={{ ...TH, textAlign: 'right' }}>{t('Pièces')}</th>
                  <th style={TH}>{t('État')}</th>
                  <th style={TH}>{t('Dernier scellement')}</th>
                </tr>
              </thead>
              <tbody>
                {closures.missing.map((m) => (
                  <tr key={`${m.registerId}:${m.month}`}>
                    <td style={{ ...TD, fontWeight: 700 }}>
                      {moisLisible(m.month)}
                      <span style={{ fontWeight: 400, color: 'var(--ink-500)' }}>
                        {` · ${t('il y a {n} mois', { n: m.monthsSince })}`}
                      </span>
                    </td>
                    <td style={{ ...TD, fontWeight: 700 }}>{codeCaisse(m.registerId)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.pieces}</td>
                    {/* ⚠️ 🔴 LE CONTEXTE `|clôture` N'EST PAS DÉCORATIF. « en retard » existe déjà au
                        catalogue, au sens d'une FACTURE ÉCHUE (espagnol « vencido ») : partagée, la clé
                        aurait donné ce mot-là à une clôture tardive, et rien ne l'aurait signalé — la
                        clé existe, elle est traduite, la complétude est satisfaite. */}
                    <td style={TD}>
                      {m.state === 'overdue'
                        ? <Badge tone="danger" dot>{t('en retard|clôture')}</Badge>
                        : <Badge tone="warn">{t('en attente|clôture')}</Badge>}
                    </td>
                    <td style={TD}>{moisLisible(m.lastSealedMonth)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * L'ÉTAT DES HORLOGES DES CAISSES — deux constats de nature différente, et un qui peut ne pas avoir eu
 * lieu.
 *
 * ⚠️ 🔴 « PAS MESURÉ » N'EST PAS « ZÉRO ANOMALIE », et c'est la seule chose que cette section doit tenir
 * absolument. L'écart entre l'horodatage scellé et l'horloge brute exige le fuseau du magasin : sans lui,
 * le contrôle **s'abstient**, et un vert afficherait un contrôle qui n'a pas eu lieu (§23.0).
 *
 * ⚠️ LE RECUL ET L'ÉCART NE SE FONDENT PAS. Un RECUL de séquence dit que la garantie de GDH croissant a
 * sauté — c'est grave et ça ne devrait pas arriver. Un ÉCART dit que la ligne de flottaison a **fait son
 * travail** : la pièce est valide, c'est l'appareil qu'il faut regarder. Les compter ensemble effacerait
 * la seule information qui dit quoi faire.
 */
function Horloges({ clock }: { clock: FiscalHealthView['clock'] }) {
  // ⚠️ 🔴 LE VERDICT VIENT DU SERVEUR (`ok`), il ne se recalcule pas ici. Le premier jet écrivait
  //    `recoilCount === 0 && gapCount === 0` — une SECONDE définition du même verdict, dont la
  //    divergence ne se signalerait pas (chaque moitié restant cohérente avec elle-même).
  const rien = clock.ok;
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Horloges des caisses')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {t('La caisse écrit deux temps sur chaque pièce : l’horodatage scellé, corrigé pour ne jamais reculer, et son horloge brute. Le siège constate qu’ils divergent — il ne sait pas quelle heure était la bonne.')}
      </p>

      {/* ⚠️ 🔴 L'ABSTENTION SE DIT AVANT LE RÉSULTAT : lue après un vert, elle ne change plus l'idée
          qu'on s'est faite. Sans fuseau, la moitié du contrôle n'a pas eu lieu. */}
      {!clock.clockGapChecked ? (
        <p role="status" style={{
          display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 12px', borderRadius: 'var(--r-sm)',
          margin: '0 0 12px', background: 'var(--amber-bg)', color: 'var(--ink-900)', fontSize: 'var(--t-body)',
        }}>
          <Icon name="clock" size={16} stroke={2.2} />
          <span>
            {t('L’écart entre les deux temps n’a PAS été mesuré : il exige le fuseau horaire du magasin, qui n’est pas réglé. Le contrôle du recul, lui, a bien eu lieu.')}
          </span>
        </p>
      ) : null}

      {clock.pieces === 0 ? (
        <EmptyState
          icon="clock"
          title={t('Aucune horloge à juger')}
          note={t('Aucune pièce scellée par nos caisses sur ce magasin. Les pièces reprises d’un ancien logiciel ne sont pas jugées : elles ne sont pas passées par notre scellement.')}
        />
      ) : rien ? (
        <div style={{
          display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 12px', borderRadius: 'var(--r-sm)',
          background: 'var(--teal-100)', color: 'var(--teal-800)',
        }}>
          <Icon name="check" size={16} stroke={2.2} />
          <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>
            {t('{n} pièce jugée : aucun recul, aucun écart au-delà de {v} s.', { n: clock.pieces, v: String(clock.toleranceSeconds) })}
          </span>
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 12px', borderRadius: 'var(--r-sm)',
            marginBottom: 12,
            background: clock.recoilCount > 0 ? 'var(--danger-bg)' : 'var(--amber-bg)',
            color: 'var(--ink-900)',
          }}>
            <Icon name={clock.recoilCount > 0 ? 'alert' : 'info'} size={16} stroke={2.2} />
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>
              {/* ⚠️ LE RECUL D'ABORD, MÊME S'IL EST MOINS NOMBREUX : c'est lui qui dit qu'une garantie
                  fiscale a sauté. L'écart, lui, dit que la garantie a tenu. */}
              {clock.recoilCount > 0
                ? t('{n} pièce porte un horodatage ANTÉRIEUR à celui de la pièce précédente de sa caisse.', { n: clock.recoilCount })
                : t('{n} pièce a été scellée à une heure qui s’écarte de l’horloge de sa caisse.', { n: clock.gapCount })}
            </span>
          </div>

          {clock.recoils.length > 0 ? (
            <TableHorloge
              titre={t('Reculs d’horodatage')}
              note={t('L’horodatage scellé est antérieur à celui du rang précédent. La caisse garantit l’inverse : ce constat dit que la garantie n’a pas tenu.')}
              colonne={t('Rang précédent')}
              lignes={clock.recoils.map((r) => ({
                cle: `${r.registerId}:${r.seqNo}`,
                registerId: r.registerId,
                piece: r.pieceNumber,
                quand: r.datetimeFiscal,
                valeur: horodatageLisible(r.previousDatetimeFiscal),
              }))}
              total={clock.recoilCount}
            />
          ) : null}

          {clock.gaps.length > 0 ? (
            <TableHorloge
              titre={t('Écarts avec l’horloge de la caisse')}
              note={t('Un écart négatif dit que l’horloge a reculé et que la caisse a refusé de la suivre : la pièce est valide, c’est l’appareil qu’il faut regarder.')}
              colonne={t('Écart')}
              lignes={clock.gaps.map((g) => ({
                cle: `${g.registerId}:${g.seqNo}`,
                registerId: g.registerId,
                piece: g.pieceNumber,
                quand: g.datetimeFiscal,
                valeur: ecartLisible(g.gapSeconds),
              }))}
              total={clock.gapCount}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Un tableau d'anomalies d'horloge — la même forme pour les deux constats.
 * ⚠️ 🔴 IL DIT QUAND LA LISTE EST TRONQUÉE. Le serveur ne nomme que cinq exemples PAR CAISSE ; annoncer
 * cinq lignes quand le compte en porte trois cents serait un écran qui rassure.
 */
function TableHorloge({ titre, note, colonne, lignes, total }: {
  titre: string; note: string; colonne: string; total: number;
  lignes: { cle: string; registerId: string; piece: string | null; quand: string; valeur: string }[];
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{titre}</h3>
      <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{note}</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>{t('Caisse')}</th>
              <th style={TH}>{t('Pièce')}</th>
              <th style={TH}>{t('Horodatage scellé')}</th>
              <th style={TH}>{colonne}</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.cle}>
                <td style={{ ...TD, fontWeight: 700 }}>{codeCaisse(l.registerId)}</td>
                <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>{l.piece ?? '—'}</td>
                <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>{horodatageLisible(l.quand)}</td>
                <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>{l.valeur}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > lignes.length ? (
        <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
          {t('{n} au total — seuls les cinq premiers de chaque caisse sont nommés.', { n: total })}
        </p>
      ) : null}
    </div>
  );
}

/** Le battement des caisses — trois états, jamais deux. */
function Battement({ registers, seuil }: { registers: RegisterLiveness[]; seuil: number }) {
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Caisses déclarées')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {t('Une caisse est dite en ligne si elle a parlé au siège depuis moins de {n} minute.', { n: seuil })}
      </p>
      {registers.length === 0 ? (
        <EmptyState
          icon="tablet"
          title={t('Aucune caisse déclarée')}
          note={t('Aucun appareil ne s’est encore présenté au siège pour ce magasin, et aucun n’y est déclaré.')}
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>{t('Caisse')}</th>
                <th style={TH}>{t('État')}</th>
                <th style={TH}>{t('Dernier contact')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('Canaux')}</th>
              </tr>
            </thead>
            <tbody>
              {registers.map((r) => (
                <tr key={r.registerId}>
                  {/* ⚠️ Le numéro passe par le formateur partagé : la base porte « 01 », l'utilisateur
                      lit « R01 » partout ailleurs, et un rendu brut afficherait « 1 » ici seulement. */}
                  <td style={{ ...TD, fontWeight: 700 }}>{codeCaisse(r.registerId)}</td>
                  <td style={TD}><EtatCaisse online={r.online} /></td>
                  <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>
                    {r.lastSeenAt === null ? '—' : instantLisible(r.lastSeenAt)}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.channels}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * L'ÉTAT D'UNE CAISSE — trois valeurs, parce que le serveur en rend trois.
 *
 * ⚠️ 🔴 `null` NE SE REND PAS COMME « HORS LIGNE ». Une caisse jamais vue est celle qu'on cherche le
 * jour de sa mise en service ; la déclarer hors ligne ferait chercher une panne de réseau là où
 * l'appareil n'a simplement jamais été branché.
 */
function EtatCaisse({ online }: { online: boolean | null }) {
  if (online === null) return <Badge tone="neutral">{t('jamais vue')}</Badge>;
  return online
    ? <Badge tone="ok" dot>{t('en ligne')}</Badge>
    : <Badge tone="warn" dot>{t('hors ligne')}</Badge>;
}

/** Le détail de chaîne, caisse par caisse — seulement les caisses qui ont scellé. */
function ChaineParCaisse({ registers }: { registers: RegisterChainHealth[] }) {
  if (registers.length === 0) return null;
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Chaîne, caisse par caisse')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {/* ⚠️ On DIT pourquoi une caisse déclarée peut manquer ici : sinon son absence se lit comme une
            perte de données, alors qu'elle veut dire « elle n'a rien vendu ». */}
        {t('Seules les caisses qui ont scellé au moins une pièce y figurent — une caisse déclarée mais qui n’a rien vendu n’a aucune chaîne à vérifier.')}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>{t('Caisse')}</th>
              <th style={{ ...TH, textAlign: 'right' }}>{t('Pièces')}</th>
              <th style={TH}>{t('Dernière pièce')}</th>
              <th style={TH}>{t('Constat')}</th>
            </tr>
          </thead>
          <tbody>
            {registers.map((r) => (
              <tr key={r.registerId}>
                <td style={{ ...TD, fontWeight: 700 }}>{codeCaisse(r.registerId)}</td>
                <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.pieces}</td>
                <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>
                  {r.lastPieceNumber ?? '—'}
                  {r.lastDatetimeFiscal ? (
                    <span style={{ color: 'var(--ink-500)' }}>{` · ${horodatageLisible(r.lastDatetimeFiscal)}`}</span>
                  ) : null}
                </td>
                <td style={TD}>
                  {r.ok ? <Badge tone="ok" dot>{t('continue')}</Badge> : <Defauts r={r} />}
                  {repriseDeclaree(r) && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: '12px', color: 'var(--ink-700)' }}>{repriseDeclaree(r)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * LES SIGNATURES DES PIÈCES — vérifiées avec la clé de chaque caisse, et les colonnes confrontées à la charge signée.
 *
 * ⚠️ CE QUI N'EST PAS ENCORE VÉRIFIÉ SE VÉRIFIE ICI, PAR LOTS, ET LE RESTE SE COMPTE : le compte vient du serveur à chaque
 *    lot — jamais une progression simulée (§23.0). Un lot qui n'avance plus arrête la boucle.
 * ⚠️ « SANS CLÉ » N'EST PAS UNE FAUTE : la caisse n'a pas encore remonté sa clé publique. Seules une signature invalide et
 *    une colonne discordante se listent comme des pièces à examiner.
 */
function Signatures({ signatures, onVerified }: { signatures: FiscalHealthView['signatures']; onVerified: () => void }) {
  const [reste, setReste] = useState<number | null>(null);
  const [echec, setEchec] = useState<string | null>(null);
  const [arrete, setArrete] = useState<number | null>(null);
  const pending = 'unavailable' in signatures ? 0 : signatures.pending;

  useEffect(() => {
    if (pending === 0) return undefined;
    // ⚠️ LA BOUCLE S'ARRÊTE AU DÉMONTAGE : un magasin changé entre deux lots ne doit pas recevoir les lots de l'autre.
    let vivant = true;
    void (async () => {
      setEchec(null);
      setArrete(null);
      setReste(pending);
      try {
        for (;;) {
          const r = await api.verifyFiscalSignatures();
          if (!vivant) return;
          setReste(r.pending);
          if (r.pending === 0) break;
          // ⚠️ UN LOT QUI N'A RIEN FAIT ARRÊTE TOUT, SANS RELIRE : la relecture remonterait la section avec le même reste, qui
          //    relancerait la boucle — à l'infini. Le reste bloqué se dit, avec son compte.
          if (r.checked + r.rechecked === 0) { setArrete(r.pending); return; }
        }
        if (vivant) onVerified();
      } catch (e: unknown) {
        if (vivant) setEchec(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { vivant = false; };
  }, [pending, onVerified]);

  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Signatures des pièces')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {t('Chaque pièce qu’une caisse a signée est vérifiée avec la clé publique que cette caisse a remontée, et ses colonnes au siège sont confrontées à la charge signée.')}
      </p>
      {'unavailable' in signatures ? (
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>
          {t('Vérification des signatures illisible : {motif}', { motif: signatures.unavailable })}
        </p>
      ) : <SignaturesLues s={signatures} reste={reste} echec={echec} arrete={arrete} />}
    </section>
  );
}

/** Ce que la section montre une fois les constats lus : l'avancement, les pièces à examiner, le tableau par caisse. */
function SignaturesLues({ s, reste, echec, arrete }: { s: SignatureHealthView; reste: number | null; echec: string | null; arrete: number | null }) {
  return (
    <>
      {s.keys === 0 && (
        <p style={{ margin: '0 0 10px' }}>
          <Badge tone="warn" dot>{t('Aucune clé publique reçue des caisses : aucune signature ne peut encore être prouvée.')}</Badge>
        </p>
      )}
      {echec ? (
        <p role="alert" style={{ margin: '0 0 10px', fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>
          {t('Vérification interrompue : {motif}', { motif: echec })}
        </p>
      ) : arrete !== null ? (
        <p role="alert" style={{ margin: '0 0 10px', fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>
          {t('{n} pièce n’a pas pu être vérifiée — la vérification s’est arrêtée', { n: arrete })}
        </p>
      ) : s.pending > 0 ? (
        <p style={{ margin: '0 0 10px', fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>
          {t('{n} pièce à vérifier — vérification en cours', { n: reste ?? s.pending })}
        </p>
      ) : null}
      {s.faultCount > 0 ? (
        <div style={{ margin: '0 0 12px' }}>
          <Badge tone="danger" dot>{t('{n} pièce à examiner', { n: s.faultCount })}</Badge>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {s.faults.map((f) => (
              <li key={`${f.registerId}-${f.seqNo}`} style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>
                <strong>{codeCaisse(f.registerId)}</strong>{` · ${f.pieceNumber ?? '—'} · `}{fauteDeSignature(f)}
              </li>
            ))}
          </ul>
          {s.faultCount > s.faults.length && (
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--ink-500)' }}>
              {t('{n} autre pièce à examiner, non listée', { n: s.faultCount - s.faults.length })}
            </p>
          )}
        </div>
      ) : s.pending === 0 && s.registers.length > 0 ? (
        <p style={{ margin: '0 0 12px' }}><Badge tone="ok" dot>{t('Aucune signature invalide, aucune colonne discordante.')}</Badge></p>
      ) : null}
      {s.registers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>{t('Caisse')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('Vérifiées')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('Sans clé')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('Invalides')}</th>
                <th style={TH}>{t('Concordance des colonnes')}</th>
              </tr>
            </thead>
            <tbody>
              {s.registers.map((r) => (
                <tr key={r.registerId}>
                  <td style={{ ...TD, fontWeight: 700 }}>{codeCaisse(r.registerId)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.signatures.valide}</td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.signatures.cle_absente}</td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.signatures.invalide > 0 ? 'var(--danger)' : undefined }}>{r.signatures.invalide}</td>
                  <td style={TD}>{concordance(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Ce qu'une pièce à examiner a de fautif — la signature, la concordance, ou les deux. */
export function fauteDeSignature(f: SignatureHealthView['faults'][number]): string {
  const motifs: string[] = [];
  if (f.signature === 'invalide') motifs.push(t('signature invalide — le contenu ne correspond plus à ce qui a été signé'));
  if (f.payload === 'mismatch') {
    motifs.push(f.field ? t('colonne discordante de la charge signée : {champ}', { champ: f.field }) : t('colonnes discordantes de la charge signée'));
  }
  return motifs.join(' · ');
}

/** La concordance d'une caisse en une ligne — discordantes d'abord, puis ce qu'on n'a pas pu conclure. */
function concordance(r: SignatureHealthView['registers'][number]): React.ReactNode {
  if (r.payload.mismatch > 0) return <Badge tone="danger" dot>{t('{n} discordante', { n: r.payload.mismatch })}</Badge>;
  if (r.payload.unverifiable > 0) return <span style={{ color: 'var(--ink-600)' }}>{t('{n} non vérifiable', { n: r.payload.unverifiable })}</span>;
  return <Badge tone="ok" dot>{t('concordante')}</Badge>;
}

/**
 * LES DÉFAUTS D'UNE CAISSE, NOMMÉS — jamais « chaîne cassée » tout court.
 *
 * ⚠️ UN TROU DE SÉQUENCE ET UN MAILLON ROMPU N'ONT PAS LA MÊME CAUSE : le premier dit qu'une pièce
 * n'est pas arrivée au siège, le second que le report ne correspond pas. Les fondre enlèverait
 * l'information qui désigne où chercher.
 */
function Defauts({ r }: { r: RegisterChainHealth }) {
  const lignes = defautsDeChaine(r);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <Badge tone="danger" dot>{t('à examiner')}</Badge>
      <span style={{ fontSize: '12px', color: 'var(--ink-700)' }}>{lignes.join(' · ')}</span>
    </span>
  );
}

/** Les défauts d'une caisse, un par cause — ce que la colonne « Constat » écrit sous « à examiner ». */
export function defautsDeChaine(r: RegisterChainHealth): string[] {
  const lignes: string[] = [];
  if (r.missingSeqCount > 0) {
    lignes.push(t('{n} rang manquant', { n: r.missingSeqCount })
      + (r.missingSeq.length > 0 ? ` (${r.missingSeq.join(', ')}${r.missingSeqCount > r.missingSeq.length ? '…' : ''})` : ''));
  }
  if (r.brokenLinks > 0) {
    lignes.push(t('{n} maillon rompu', { n: r.brokenLinks })
      + (r.firstBrokenSeq !== null ? ` (${t('à partir du rang {v}', { v: String(r.firstBrokenSeq) })})` : ''));
  }
  if (r.payloadMismatch > 0) {
    lignes.push(t('{n} charge signée discordante', { n: r.payloadMismatch }));
  }
  return lignes;
}

/**
 * La reprise de chaîne DÉCLARÉE d'une caisse, ou `null`.
 * ⚠️ DITE MÊME QUAND LA CHAÎNE EST CONTINUE : une caisse remplacée a deux chaînes, et le vert seul laisserait croire
 *    qu'elle n'en a qu'une — c'est le JET 220 qui fait la continuité, et l'auditeur doit savoir où le chercher.
 */
export function repriseDeclaree(r: RegisterChainHealth): string | null {
  return r.declaredRestarts > 0 ? t('{n} reprise de chaîne déclarée (JET 220)', { n: r.declaredRestarts }) : null;
}

/** Les archives inscrites — l'état de la conservation, du plus récent au plus ancien. */
function Archives({ archives }: { archives: FiscalHealthView['archives'] }) {
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Archives inscrites')}
      </h2>
      {archives.length === 0 ? (
        <EmptyState
          icon="box"
          title={t('Aucune archive pour ce magasin')}
          note={t('Rien n’a encore été archivé. La constitution d’une archive se fait depuis l’écran « Archives fiscales ».')}
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>{t('Rang')}</th>
                <th style={TH}>{t('Période')}</th>
                <th style={TH}>{t('Constituée le')}</th>
              </tr>
            </thead>
            <tbody>
              {archives.map((a) => (
                <tr key={a.archiveId}>
                  <td style={{ ...TD, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{a.seq}</td>
                  <td style={TD}>{periodeLisible(a.periodKind, a.periodFrom)}</td>
                  <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>{horodatageLisible(a.constitutedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Un instant en millisecondes, rendu dans la langue du compte.
 * ⚠️ C'est un INSTANT (le moment où la caisse a parlé au siège), pas une date fiscale : il se lit donc
 * bien dans le fuseau du lecteur, à l'inverse d'un horodatage scellé.
 */
export function instantLisible(ms: number, lang: string = currentLang()): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(ms));
}

/**
 * `AAAAMMJJHHMMSS` rendu lisiblement.
 * ⚠️ 🔴 C'EST UNE HEURE LOCALE DU MAGASIN, scellée telle quelle : on la découpe, on ne la reconstruit
 * JAMAIS en instant. La relire comme une date la replacerait dans le fuseau du LECTEUR, et une pièce
 * scellée à 00h30 se daterait de la veille pour un lecteur à l'ouest.
 */
export function horodatageLisible(s: string): string {
  if (!/^\d{14}$/.test(s)) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

/**
 * UN ÉCART D'HORLOGE EN CLAIR — « −2 h 00 », « +45 s ».
 *
 * ⚠️ 🔴 LE SIGNE EST CONSERVÉ, ET IL PORTE TOUT LE SENS : négatif = l'horodatage scellé est en avance sur
 * l'horloge, donc la ligne de flottaison a corrigé un RECUL ; positif = l'horloge a pris de l'avance. Le
 * rendre en valeur absolue effacerait la distinction, et les deux faits n'appellent pas le même geste.
 *
 * ⚠️ L'UNITÉ SUIT L'ORDRE DE GRANDEUR : « −7 200 s » ne se lit pas, « −2 h 00 » se lit. On ne va pas
 * au-delà des heures — un écart de plusieurs jours reste plus parlant en heures qu'en jours arrondis.
 */
export function ecartLisible(secondes: number): string {
  const signe = secondes < 0 ? '−' : '+';
  const a = Math.abs(secondes);
  if (a < 60) return `${signe}${a} s`;
  if (a < 3600) return `${signe}${Math.floor(a / 60)} min ${String(a % 60).padStart(2, '0')} s`;
  return `${signe}${Math.floor(a / 3600)} h ${String(Math.floor((a % 3600) / 60)).padStart(2, '0')}`;
}

/**
 * LE BILAN D'UN CONSTAT DE CLÔTURES — combien sont EN RETARD, combien ATTENDENT.
 *
 * ⚠️ EXTRAIT POUR ÊTRE MIS EN ÉCHEC : c'est ce compte qui choisit le TON du bandeau (rouge ou ambre) et
 * la phrase affichée. Fait à l'intérieur du rendu, il ne s'éprouve pas — et la décision « un mois en
 * attente n'est PAS une alerte » est précisément celle qu'il faut verrouiller.
 *
 * ⚠️ L'ATTENTE SE DÉDUIT DU RESTE plutôt que de se compter à part : deux filtres indépendants
 * pourraient laisser un état tomber entre les deux sans que le total le dise.
 */
export function bilanClotures(missing: MissingClosure[]): { enRetard: number; enAttente: number } {
  const enRetard = missing.filter((m) => m.state === 'overdue').length;
  return { enRetard, enAttente: missing.length - enRetard };
}

/**
 * Un mois `AAAAMM` en clair — « Janvier 2026 ».
 * ⚠️ IL DÉLÈGUE au formateur de période plutôt que de refaire le rendu : deux fonctions qui nomment un
 * mois finiraient par le nommer différemment sur deux sections du MÊME écran.
 */
export function moisLisible(ym: string, lang: string = currentLang()): string {
  return periodeLisible('mensuelle', `${ym}01`, lang);
}

/** La période d'une archive, en clair — l'année seule pour une annuelle. */
export function periodeLisible(kind: string, from: string, lang: string = currentLang()): string {
  const an = from.slice(0, 4);
  if (kind === 'annuelle') return an;
  const d = new Date(Number(an), Number(from.slice(4, 6)) - 1, 1);
  const s = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(d);
  // ⚠️ La capitale est AJOUTÉE : `Intl` rend « septembre », correct dans une phrase, pas en cellule.
  return s.charAt(0).toUpperCase() + s.slice(1);
}
